import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';

import type { StepsDatabase } from '../stepsDatabase.ts';

export type EditorOfflineStoragePluginStatus = 'pending' | 'restoring' | 'ready' | 'tooManyRetries';

export type EditorOfflineStorageDisabledReason =
	| 'disabledEnvironment'
	| 'stepsDatabaseError'
	| 'tooManyRetries'
	| 'unableToSync'
	| 'indexedDBAccessError';

export type EditorOfflineStoragePluginState = {
	status: EditorOfflineStoragePluginStatus;
	disabled: EditorOfflineStorageDisabledReason | false;
	inactiveSyncing: boolean;
};

export const editorOfflineStoragePluginKey = new PluginKey<EditorOfflineStoragePluginState>(
	'editorOfflineStoragePlugin',
);

export const createPlugin = (stepsDatabase: StepsDatabase) => () => {
	return new SafePlugin<EditorOfflineStoragePluginState>({
		key: editorOfflineStoragePluginKey,
		state: {
			init() {
				// environment-aware enablement via feature encoding
				const isEditorFeatureEnabled = isFeatureEnabled(
					'confluence',
					'confluence-editor-offline-storage',
				);
				return {
					status: 'pending',
					disabled: !isEditorFeatureEnabled ? 'disabledEnvironment' : false,
					inactiveSyncing: false,
				};
			},
			apply: (tr: Transaction, currentPluginState: EditorOfflineStoragePluginState) => {
				const meta = tr.getMeta(editorOfflineStoragePluginKey);
				if (meta) {
					return {
						...currentPluginState,
						...meta,
					};
				}
				return currentPluginState;
			},
		},
		view: () => {
			return {
				destroy() {
					return stepsDatabase.closeDB(true);
				},
			};
		},
	});
};
